import ScalarCopyBackdrop_default from "../ScalarCopy/ScalarCopyBackdrop.vue.js";
import ScalarCodeBlockCopy_default from "./ScalarCodeBlockCopy.vue.js";
import { useBindCx } from "@scalar/use-hooks/useBindCx";
import { computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createVNode, defineComponent, guardReactiveProps, normalizeClass, normalizeProps, openBlock, unref, useId, withCtx } from "vue";
import { prettyPrintJson } from "@scalar/helpers/json/pretty-print-json";
import { syntaxHighlight } from "@scalar/highlight/compat";
import { languages, registerLanguage } from "@scalar/highlight/all";
//#region src/components/ScalarCodeBlock/ScalarCodeBlock.vue?vue&type=script&setup=true&lang.ts
/**
* Uses @scalar/highlight for syntax highlighting
*
* Requires at least one of content or prettyPrintedContent
*/
var _hoisted_1 = ["aria-label"];
var _hoisted_2 = ["id", "innerHTML"];
/**
* Registers every bundled grammar, once per module rather than once per block.
*
* `@scalar/highlight/all` already registers them when it is evaluated, so a
* bare `import '@scalar/highlight/all'` reads like enough. It is not: that is a
* side-effect-only import of an external package, and the library build drops
* it from the emitted module. Storybook hid this because it compiles from
* source, while the API reference consumes `dist` and lost highlighting
* entirely — every block fell back to escaped, unscoped text.
*
* Registering from a named import keeps a binding the bundler can see used, so
* the module survives. It lives in this block rather than in `<script setup>`
* because setup runs for every instance, and a page of API reference renders
* hundreds of code blocks.
*/
registerLanguage(...languages);
var ScalarCodeBlock_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	inheritAttrs: false,
	__name: "ScalarCodeBlock",
	props: {
		content: {},
		prettyPrintedContent: {},
		lang: { default: "plaintext" },
		lineNumbers: {
			type: Boolean,
			default: false
		},
		hideCredentials: {},
		copy: {
			type: [String, Boolean],
			default: "hover"
		},
		label: { default: "Code sample" },
		copyLabel: {}
	},
	setup(__props) {
		/** Base id for the code block */
		const id = useId();
		/** Formatted the content into an indented json string */
		const prettyContent = computed(() => __props.prettyPrintedContent || prettyPrintJson(__props.content ?? ""));
		const highlightedCode = computed(() => {
			return syntaxHighlight(prettyContent.value, {
				lang: __props.lang.trim(),
				lineNumbers: __props.lineNumbers,
				maskCredentials: __props.hideCredentials
			}).slice(5, -6);
		});
		/** Determine if the content is a single line */
		const isOneLine = computed(() => !prettyContent.value.includes("\n"));
		const isContentValid = computed(() => {
			return prettyContent.value !== null && prettyContent.value !== "null" && prettyContent.value !== "404 Not Found";
		});
		/** Whether the copy button is rendered on top of the code */
		const showCopy = computed(() => __props.copy && isContentValid.value);
		/**
		* A one-liner has the copy button floating over the end of the line, so the line needs room to
		* scroll clear of the button. The padding goes on the `w-fit` <pre> because browsers disagree about
		* whether trailing padding on an `overflow-x` container is scrollable.
		*/
		const reserveCopySpace = computed(() => isOneLine.value && showCopy.value);
		const { cx } = useBindCx();
		return (_ctx, _cache) => {
			return openBlock(), createElementBlock("div", normalizeProps(guardReactiveProps(unref(cx)("scalar-code-block group/code-block flex flex-col", "relative bg-b-1 min-h-0 min-w-0 focus-visible:outline"))), [createElementVNode("div", {
				"aria-label": __props.label,
				class: "custom-scroll min-h-0 min-w-0 flex-1 overflow-x-auto rounded-[inherit] p-2 -outline-offset-2",
				role: "group",
				tabindex: "0"
			}, [createElementVNode("pre", {
				id: unref(id),
				class: normalizeClass(["m-0 w-fit border-0 bg-transparent p-0 text-nowrap whitespace-pre", { "pr-6": reserveCopySpace.value }]),
				innerHTML: highlightedCode.value
			}, null, 10, _hoisted_2)], 8, _hoisted_1), showCopy.value ? (openBlock(), createBlock(unref(ScalarCodeBlockCopy_default), {
				key: 0,
				"aria-controls": unref(id),
				class: normalizeClass(["scalar-code-copy absolute", [isOneLine.value ? "top-1/2 right-1 m-0 -translate-y-1/2" : "top-2.5 right-2.5", { "opacity-100": __props.copy === "always" }]]),
				content: prettyContent.value,
				copyLabel: __props.copyLabel,
				lang: __props.lang,
				showLang: !isOneLine.value
			}, {
				backdrop: withCtx(() => [createVNode(ScalarCopyBackdrop_default, { class: normalizeClass(["scalar-code-copy-backdrop", [isOneLine.value ? "group-hocus-within/code-block:-left-0.5 -inset-y-0.75 -right-1 left-3" : "-top-1 -right-1.5"]]) }, null, 8, ["class"])]),
				_: 1
			}, 8, [
				"aria-controls",
				"class",
				"content",
				"copyLabel",
				"lang",
				"showLang"
			])) : createCommentVNode("", true)], 16);
		};
	}
});
//#endregion
export { ScalarCodeBlock_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=ScalarCodeBlock.vue_vue_type_script_setup_true_lang.js.map